import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';

/* /job-seekers
   SEO title: Find Your Dream Job in India – Register Free | PlanB Jobs */

const PROFILE_FIELDS = [
  'Personal information', 'Education', 'Work experience', 'Skills',
  'Career preferences', 'Expected salary', 'Preferred locations', 'Resume upload',
];
const SKILLS = ['Figma', 'UX research', 'Prototyping', '3 yrs experience'];
const MATCHES = [
  { role: 'UI Designer', meta: 'Fintech startup · Bengaluru', score: 92 },
  { role: 'Product Designer', meta: 'SaaS company · Remote', score: 86 },
  { role: 'UX Researcher', meta: 'E-commerce · Hyderabad', score: 78 },
];
const STEPS = [
  { label: 'Application sent', note: 'Product Designer', state: 'done' },
  { label: 'Profile viewed', note: 'By the employer', state: 'done' },
  { label: 'Under review', note: 'In progress', state: 'now' },
];
const LEVELS = ['Freshers', 'Mid-level', 'Senior', 'Full-time', 'Part-time', 'Contract', 'Internships'];
const JOURNEY = [
  { n: '01', title: 'Create your profile', body: 'Add your skills, experience and preferences in a few minutes.' },
  { n: '02', title: 'Discover relevant jobs', body: 'See vacancies that fit your profile, skills and preferred location.' },
  { n: '03', title: 'Apply and track', body: 'Apply in a few taps and follow your progress from your dashboard.' },
];
const COMPLETENESS = 85;

function useSeo(title, description) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    const prev = meta.getAttribute('content');
    meta.setAttribute('content', description);
    return () => {
      document.title = prevTitle;
      if (created) meta.remove(); else if (prev !== null) meta.setAttribute('content', prev);
    };
  }, [title, description]);
}

/* ---------------------------- animation helpers ---------------------------- */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) { setOn(true); return; }
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setOn(true); io.disconnect(); } },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, on];
}

function Reveal({ children, delay = 0, className = '', variant = 'up' }) {
  const [ref, on] = useInView(0.12);
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`reveal ${variant !== 'up' ? `reveal-${variant}` : ''} ${on ? 'reveal-in' : ''} ${className}`}
    >
      {children}
    </div>
  );
}

function ScrollProgress() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setP(h > 0 ? (window.scrollY / h) * 100 : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <div className="fixed top-0 left-0 h-[3px] z-[100] brand-gradient" style={{ width: `${p}%` }} aria-hidden="true" />;
}

function Check({ className = 'w-3 h-3' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

/* bar that fills when it scrolls into view */
function Bar({ label, value, delay = 0 }) {
  const [ref, on] = useInView(0.3);
  return (
    <div ref={ref}>
      <div className="flex justify-between text-sm mb-1.5"><span>{label}</span><b>{value}%</b></div>
      <div className="h-2 rounded-full bg-white/15 overflow-hidden">
        <div
          className="h-full rounded-full bg-[#C7F464] transition-[width] duration-[1400ms] ease-out"
          style={{ width: on ? `${value}%` : '0%', transitionDelay: `${delay}ms` }}
        />
      </div>
    </div>
  );
}

/* ring that draws itself when it scrolls into view */
function CompletenessRing({ value }) {
  const [ref, on] = useInView(0.3);
  const r = 70;
  const c = 2 * Math.PI * r;
  return (
    <div ref={ref} className="relative w-44 h-44 sm:w-52 sm:h-52 mx-auto">
      <svg viewBox="0 0 180 180" className="w-full h-full -rotate-90" aria-hidden="true">
        <defs>
          <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#7C3AED" />
            <stop offset="100%" stopColor="#DB2777" />
          </linearGradient>
        </defs>
        <circle cx="90" cy="90" r={r} fill="none" stroke="#F0E8FA" strokeWidth="14" />
        <circle
          cx="90" cy="90" r={r} fill="none" stroke="url(#ringGrad)" strokeWidth="14" strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={on ? c * (1 - value / 100) : c}
          style={{ transition: 'stroke-dashoffset 1.8s cubic-bezier(.2,.8,.2,1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl sm:text-5xl font-extrabold text-[#7C3AED] tabular-nums">{value}%</span>
        <span className="text-xs font-semibold text-gray-500 mt-1">Profile strength</span>
      </div>
    </div>
  );
}

function ProfileChecklist() {
  const [ref, on] = useInView(0.2);
  return (
    <ul ref={ref} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {PROFILE_FIELDS.map((f, i) => (
        <li
          key={f}
          style={{ transitionDelay: `${i * 110}ms` }}
          className={`flex items-center gap-3 bg-white border border-purple-100 rounded-2xl px-4 py-3 text-sm font-semibold shadow-sm transition-all duration-500 hover:-translate-y-0.5 hover:shadow-md ${on ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
        >
          <span
            style={{ transitionDelay: `${i * 110 + 250}ms` }}
            className={`w-6 h-6 rounded-full brand-gradient text-white flex items-center justify-center shrink-0 transition-transform duration-500 ${on ? 'scale-100' : 'scale-0'}`}
          >
            <Check />
          </span>
          {f}
        </li>
      ))}
    </ul>
  );
}

/* ---------------------------------- page ---------------------------------- */
function JobSeekers() {
  const [user] = useState(() => {
    const s = localStorage.getItem('user');
    return s ? JSON.parse(s) : null;
  });
  const isLoggedIn = !!localStorage.getItem('token');
  const profileLink = isLoggedIn ? '/profile' : '/register';

  useSeo(
    'Find Your Dream Job in India – Register Free | PlanB Jobs',
    'Create your free job seeker profile on PlanB Jobs. Discover jobs, connect with employers and explore career opportunities for freshers and experienced professionals.'
  );

  return (
    <div className="seekers-root min-h-screen bg-white overflow-x-clip text-[#1E0F3D]">
      <style>{`
        .brand-gradient { background-image: linear-gradient(90deg,#7C3AED,#DB2777); }
        .seekers-root h2 { display:inline-block; background-image:linear-gradient(90deg,#7C3AED,#DB2777); -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; color:transparent; }
        .seekers-root .brand-gradient h2, .seekers-root .animated-gradient h2 { background-image:none; -webkit-text-fill-color:#fff; color:#fff; }
        .animated-gradient { background:linear-gradient(90deg,#7C3AED,#DB2777,#7C3AED); background-size:200% 100%; animation:gradient-shift 8s ease infinite; }
        .marquee-mask { -webkit-mask-image:linear-gradient(90deg,transparent,#000 10%,#000 90%,transparent); mask-image:linear-gradient(90deg,transparent,#000 10%,#000 90%,transparent); }

        .reveal { opacity:0; transform:translateY(32px); transition:opacity .7s cubic-bezier(.2,.8,.2,1), transform .7s cubic-bezier(.2,.8,.2,1); }
        .reveal-left { transform:translateX(-56px); }
        .reveal-right { transform:translateX(56px); }
        .reveal-zoom { transform:scale(.92); }
        .reveal-in { opacity:1; transform:none; }

        @keyframes rise { from { opacity:0; transform:translateY(20px);} to { opacity:1; transform:none;} }
        @keyframes float { 0%,100% { transform:translateY(0) scale(1);} 50% { transform:translateY(-24px) scale(1.06);} }
        @keyframes bob { 0%,100% { transform:translateY(0) rotate(2deg);} 50% { transform:translateY(-10px) rotate(-1deg);} }
        @keyframes shimmer { to { background-position:200% center; } }
        @keyframes gradient-shift { 0%,100% { background-position:0% 50%;} 50% { background-position:100% 50%;} }
        @keyframes marquee-ltr { from { transform:translateX(0);} to { transform:translateX(-50%);} }
        @keyframes marquee-rtl { from { transform:translateX(-50%);} to { transform:translateX(0);} }
        @keyframes pulse-ring { 0% { box-shadow:0 0 0 0 rgba(124,58,237,.45);} 100% { box-shadow:0 0 0 16px rgba(124,58,237,0);} }
        @keyframes cta-glow { 0% { box-shadow:0 0 0 0 rgba(255,255,255,.5);} 100% { box-shadow:0 0 0 18px rgba(255,255,255,0);} }
        @keyframes scan { 0% { transform:translateY(0); opacity:0;} 15% { opacity:1;} 85% { opacity:1;} 100% { transform:translateY(120px); opacity:0;} }

        .rise { animation:rise .8s cubic-bezier(.2,.8,.2,1) both; }
        .float-slow { animation:float 9s ease-in-out infinite; }
        .float-slower { animation:float 13s ease-in-out infinite reverse; }
        .bob { animation:bob 5s ease-in-out infinite; }
        .shimmer-text { background-size:200% auto; animation:shimmer 5s linear infinite; }
        .marquee-ltr { animation:marquee-ltr 35s linear infinite; }
        .marquee-rtl { animation:marquee-rtl 45s linear infinite; }
        .pulse-ring { animation:pulse-ring 2s ease-out infinite; }
        .cta-glow { animation:cta-glow 2.4s ease-out infinite; }
        .scan-line { animation:scan 3s ease-in-out infinite; }

        .shine-btn { position:relative; overflow:hidden; }
        .shine-btn::after { content:''; position:absolute; top:0; left:0; width:30%; height:100%; background:rgba(255,255,255,.3); transform:translateX(-120%) skewX(-20deg); transition:transform .7s ease; pointer-events:none; }
        .shine-btn:hover::after { transform:translateX(420%) skewX(-20deg); }

        @media (prefers-reduced-motion: reduce) {
          .rise,.float-slow,.float-slower,.bob,.shimmer-text,.marquee-ltr,.marquee-rtl,.pulse-ring,.cta-glow,.scan-line,.animated-gradient { animation:none; }
          .reveal { opacity:1; transform:none; transition:none; }
          .shine-btn::after { transition:none; }
        }
      `}</style>

      <ScrollProgress />
      <Navbar user={user} />

      {/* ================================ HERO ================================ */}
      <section className="relative bg-gradient-to-br from-[#FBF5FF] via-white to-[#FDF0F6] pt-14 pb-24 lg:pt-20 lg:pb-28">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute -top-40 -right-24 w-[36rem] h-[36rem] rounded-full bg-[#7C3AED]/15 blur-3xl float-slow" />
          <div className="absolute -bottom-40 -left-24 w-[30rem] h-[30rem] rounded-full bg-[#DB2777]/12 blur-3xl float-slower" />
          <div className="absolute inset-0 opacity-60" style={{ backgroundImage: 'radial-gradient(rgba(124,58,237,0.10) 1px, transparent 1px)', backgroundSize: '26px 26px' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-14 items-center">
          <div>
            <div className="rise inline-flex items-center gap-2 mb-5 bg-white border border-purple-100 rounded-full px-4 py-1.5 text-xs font-semibold text-gray-600 shadow-sm">
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full rounded-full bg-[#DB2777] opacity-75 animate-ping motion-reduce:animate-none" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-[#DB2777]" />
              </span>
              Free for freshers and experienced professionals
            </div>

            <h1 className="mb-6 text-[40px] sm:text-[56px] font-extrabold leading-[1.05] tracking-tight">
              {['Your Career.', 'Your Choices.', 'Your Next Move.'].map((t, i) => (
                <span
                  key={t}
                  style={{ animationDelay: `${0.08 + i * 0.12}s` }}
                  className="rise shimmer-text block w-fit bg-gradient-to-r from-[#7C3AED] via-[#DB2777] to-[#7C3AED] bg-clip-text text-transparent"
                >
                  {t}
                </span>
              ))}
            </h1>

            <p className="rise max-w-lg text-lg leading-7 text-gray-600" style={{ animationDelay: '.45s' }}>
              Your skills deserve the right opportunity. Create your profile, discover relevant vacancies and connect with employers who are looking for people like you.
            </p>

            <div className="rise flex flex-wrap gap-3 mt-8" style={{ animationDelay: '.58s' }}>
              <Link to={profileLink} className="shine-btn brand-gradient pulse-ring text-white px-8 py-3.5 rounded-xl font-semibold text-sm shadow-[0_10px_30px_-8px_rgba(124,58,237,0.6)] transition hover:-translate-y-0.5">
                Create My Free Profile
              </Link>
              <a href="#how" className="px-8 py-3.5 rounded-xl font-semibold text-sm text-[#7C3AED] bg-white border border-[#7C3AED]/40 hover:bg-[#F5F0FA] hover:-translate-y-0.5 transition">
                See how it works
              </a>
            </div>
          </div>

          {/* profile mock */}
          <div className="relative max-w-md w-full rise lg:justify-self-end" style={{ animationDelay: '.25s' }} aria-hidden="true">
            <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-[#7C3AED]/25 to-[#DB2777]/20 blur-3xl" />

            <div className="relative bg-white rounded-3xl border border-purple-100 shadow-[0_40px_80px_-30px_rgba(124,58,237,0.45)] p-5">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-full brand-gradient text-white flex items-center justify-center font-bold pulse-ring">AR</div>
                <div>
                  <p className="font-bold">Aarav Rao</p>
                  <p className="text-sm text-gray-500">Product Designer · Bengaluru</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {SKILLS.map((s, i) => (
                  <span key={s} style={{ animationDelay: `${0.6 + i * 0.1}s` }} className="rise text-xs font-medium text-[#7C3AED] bg-[#F5F0FA] rounded-full px-3 py-1">{s}</span>
                ))}
              </div>

              <div className="mt-4 divide-y divide-purple-50">
                {MATCHES.map((j, i) => (
                  <div key={j.role} style={{ animationDelay: `${0.9 + i * 0.18}s` }} className="rise flex items-center justify-between gap-3 py-3">
                    <div>
                      <p className="font-semibold text-sm">{j.role}</p>
                      <p className="text-xs text-gray-500">{j.meta}</p>
                    </div>
                    <span className="text-xs font-bold text-white brand-gradient rounded-lg px-2.5 py-1 whitespace-nowrap">{j.score}% match</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bob absolute -bottom-7 -right-2 w-56 brand-gradient text-white rounded-2xl p-4 shadow-xl">
              <p className="text-xs text-white/80">Application update</p>
              <p className="font-bold text-sm">Shortlisted for interview</p>
            </div>

            <div className="float-slow absolute -top-5 -left-4 bg-white border border-purple-100 rounded-2xl px-3.5 py-2 shadow-lg flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#C7F464] text-[#17142A] flex items-center justify-center"><Check /></span>
              <span className="text-xs font-bold">Profile viewed</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ SCROLLING STRIP ============================ */}
      <div className="marquee-mask overflow-hidden py-8 space-y-3">
        <div className="marquee-ltr flex w-max">
          {[...LEVELS, ...LEVELS, ...LEVELS, ...LEVELS].map((l, i) => (
            <span key={i} className="shrink-0 mr-3 px-5 py-2 rounded-full bg-white border border-purple-100 text-sm font-semibold text-[#7C3AED] shadow-sm">{l}</span>
          ))}
        </div>
        <div className="marquee-rtl flex w-max">
          {[...LEVELS, ...LEVELS, ...LEVELS, ...LEVELS].reverse().map((l, i) => (
            <span key={i} className="shrink-0 mr-3 px-5 py-2 rounded-full bg-[#F5F0FA] text-sm font-semibold text-[#DB2777]">{l}</span>
          ))}
        </div>
      </div>

      {/* ============================== HOW IT WORKS ============================== */}
      <section id="how" className="scroll-mt-24 max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Three steps to your next role</h2>
            <p className="text-gray-500">Build your profile once and let relevant opportunities come to you.</p>
          </div>
        </Reveal>

        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="hidden md:block absolute top-9 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-[#7C3AED] to-[#DB2777] opacity-30" aria-hidden="true" />
          {JOURNEY.map((s, i) => (
            <Reveal key={s.n} delay={i * 150} className="h-full">
              <div className="group relative h-full text-center rounded-3xl p-7 bg-white border border-purple-100 transition-all duration-300 hover:-translate-y-2 hover:border-[#DB2777]/40 hover:shadow-[0_30px_60px_-25px_rgba(124,58,237,0.5)]">
                <div className="relative mx-auto w-[4.5rem] h-[4.5rem] rounded-2xl brand-gradient text-white flex items-center justify-center text-2xl font-extrabold shadow-lg shadow-[#7C3AED]/30 transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110">
                  {s.n}
                </div>
                <h3 className="text-lg font-bold mt-5 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================================ FEATURES ================================ */}
      <section id="features" className="scroll-mt-24 bg-gradient-to-br from-[#FBF5FF] to-[#FDF0F6] py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Everything you need to find the right role</h2>
            <p className="text-gray-500 max-w-xl mb-12">One profile, one dashboard. Everything in one place.</p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
            {/* discover */}
            <Reveal variant="left" className="md:col-span-3 h-full">
              <article className="h-full rounded-3xl p-7 brand-gradient text-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-25px_rgba(124,58,237,0.6)]">
                <h3 className="text-xl font-bold mb-2">Discover relevant jobs</h3>
                <p className="text-sm text-white/85">Explore vacancies based on your profile, skills, experience and preferred location.</p>
                <div className="relative mt-6 h-28 flex items-center justify-center">
                  <span className="absolute w-20 h-20 rounded-full border-2 border-white/60 animate-ping motion-reduce:animate-none" />
                  <svg viewBox="0 0 200 110" className="relative w-44 float-slow" aria-hidden="true">
                    <circle cx="86" cy="50" r="32" fill="none" stroke="#fff" strokeOpacity=".9" strokeWidth="8" />
                    <line x1="110" y1="74" x2="148" y2="104" stroke="#fff" strokeWidth="10" strokeLinecap="round" />
                    <circle cx="86" cy="50" r="12" fill="#fff" />
                  </svg>
                </div>
              </article>
            </Reveal>

            {/* apply and track */}
            <Reveal variant="right" className="md:col-span-3 h-full">
              <article className="h-full rounded-3xl p-7 bg-[#17142A] text-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-25px_rgba(23,20,42,0.7)]">
                <h3 className="text-xl font-bold mb-2">Apply and track</h3>
                <p className="text-sm text-white/70">Apply for vacancies and follow your progress from your candidate dashboard.</p>
                <div className="relative mt-6 space-y-5">
                  <span className="absolute left-[7px] top-3 bottom-3 w-0.5 bg-white/15" aria-hidden="true" />
                  {STEPS.map((s, i) => (
                    <Reveal key={s.label} delay={i * 200}>
                      <div className="relative flex items-start gap-3.5 text-sm">
                        <span className={`mt-1 w-4 h-4 rounded-full border-2 shrink-0 ${s.state === 'done' ? 'bg-[#C7F464] border-[#C7F464]' : 'border-[#C7F464] bg-[#17142A] animate-pulse motion-reduce:animate-none shadow-[0_0_0_4px_rgba(199,244,100,0.25)]'}`} />
                        <div>{s.label}<span className="block text-xs text-white/60">{s.note}</span></div>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </article>
            </Reveal>

            {/* build career */}
            <Reveal className="md:col-span-2 h-full">
              <article className="h-full rounded-3xl p-7 border border-purple-100 bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-25px_rgba(124,58,237,0.5)]">
                <h3 className="text-xl font-bold mb-2">Build your career</h3>
                <p className="text-sm text-gray-500">Find opportunities across industries, experience levels and employment types.</p>
                <div className="flex flex-wrap gap-2 mt-6">
                  {LEVELS.map((l, i) => (
                    <span
                      key={l}
                      style={{ animationDelay: `${i * 0.15}s` }}
                      className="bob text-sm bg-[#FBF5FF] border border-purple-100 rounded-full px-3.5 py-1.5 transition hover:bg-[#F5F0FA] hover:border-[#7C3AED]/40"
                    >
                      {l}
                    </span>
                  ))}
                </div>
              </article>
            </Reveal>

            {/* AI recommendations */}
            <Reveal delay={120} className="md:col-span-4 h-full">
              <article className="relative overflow-hidden h-full rounded-3xl p-7 lg:p-9 bg-[#17142A] text-white grid grid-cols-1 md:grid-cols-2 gap-8 items-center transition-all duration-300 hover:-translate-y-1.5">
                <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-[#7C3AED]/30 blur-3xl float-slow pointer-events-none" aria-hidden="true" />
                <div className="relative">
                  <h3 className="text-xl font-bold mb-2">AI-powered recommendations</h3>
                  <p className="text-sm text-white/70 max-w-md">Explore relevant job recommendations using available matching capabilities, based on what you add to your profile.</p>
                </div>
                <div className="relative">
                  <div className="space-y-3.5">
                    {[['Product Designer', 92], ['UI Designer', 86], ['UX Researcher', 78]].map(([r, n], i) => (
                      <Bar key={r} label={r} value={n} delay={i * 200} />
                    ))}
                  </div>
                  <p className="text-xs text-white/50 mt-4">Sample recommendations for illustration.</p>
                </div>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============================ PROFILE STRENGTH ============================ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-16 items-center">
          <Reveal variant="left">
            <div className="rounded-3xl border border-purple-100 bg-gradient-to-br from-[#FBF5FF] to-white p-8 text-center shadow-[0_30px_70px_-35px_rgba(124,58,237,0.45)]">
              <CompletenessRing value={COMPLETENESS} />
              <p className="mt-5 text-sm text-gray-500">The more you add, the better your matches.</p>
              <p className="text-xs text-gray-400 mt-1">Illustration of a sample profile.</p>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Create your professional profile</h2>
              <p className="text-gray-500 max-w-lg mb-8">Tell employers who you are and what you are looking for. Add as much or as little as you like.</p>
            </Reveal>
            <ProfileChecklist />
          </div>
        </div>
      </section>

      {/* ================================== CTA ================================== */}
      <section className="px-6 lg:px-8 pb-20 lg:pb-24">
        <Reveal variant="zoom">
          <div className="max-w-7xl mx-auto animated-gradient rounded-[2rem] relative overflow-hidden px-8 lg:px-16 py-14 text-center shadow-[0_40px_80px_-30px_rgba(124,58,237,0.6)]">
            <div className="absolute inset-0 opacity-15 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '22px 22px' }} aria-hidden="true" />
            <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-white/20 blur-3xl float-slow pointer-events-none" aria-hidden="true" />
            <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full bg-white/15 blur-3xl float-slower pointer-events-none" aria-hidden="true" />
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Your Next Opportunity Starts With One Click.</h2>
              <p className="text-white/85 mb-8">Set up your profile in minutes. Registration is free.</p>
              <Link to={profileLink} className="cta-glow inline-block bg-white text-[#7C3AED] px-9 py-4 rounded-xl font-bold transition hover:-translate-y-1 hover:shadow-xl">
                Create My Free Profile
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}

export default JobSeekers;